import React, { useContext, useEffect, useRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { AuthContext } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Kicker, AmberButton, EngGrid } from '../components/design';

const MONO = "'IBM Plex Mono', monospace";



// ── Compact scan stat (synced with scan beam, used inside DetectionPanel) ────
// startDelay: ms before animation begins (synced to scan beam)
// fadeIn: true = skip count-up, just fade the whole stat in after startDelay
function ScanStat({ value, label, color = '#2dd4bf', decimals = 0, suffix = '', startDelay = 330, fadeIn = false }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fmt = v => decimals > 0 ? v.toFixed(decimals) : Math.round(v).toLocaleString();
    el.textContent = fmt(value); // always pre-fill so fadeIn has content to show
    if (fadeIn || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = setTimeout(() => {
      el.textContent = '0';
      const start = performance.now();
      const ease = t => 1 - Math.pow(1 - t, 3);
      let raf;
      const tick = now => {
        const t = Math.min(1, (now - start) / 4200);
        el.textContent = fmt(value * ease(t));
        if (t < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }, startDelay);
    return () => clearTimeout(timer);
  }, [value, decimals, fadeIn, startDelay]);
  return (
    <Box sx={{
      textAlign: 'center',
      ...(fadeIn ? {
        opacity: 0,
        animation: `fadeStatIn .5s ease ${startDelay}ms forwards`,
        '@keyframes fadeStatIn': { to: { opacity: 1 } },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 1 },
      } : {}),
    }}>
      <Box sx={{ fontFamily: MONO, fontSize: '15px', fontWeight: 600, color, display: 'inline-flex', alignItems: 'baseline', gap: '2px' }}>
        <span ref={ref} />
        {suffix && <Box component="span" sx={{ fontSize: '10px' }}>{suffix}</Box>}
      </Box>
      <Box sx={{ fontFamily: MONO, fontSize: '9.5px', letterSpacing: '.13em', textTransform: 'uppercase', color: '#5f7398', mt: '4px' }}>{label}</Box>
    </Box>
  );
}

// ── Detection panel SVG ──────────────────────────────────────────────────────
const DetectionPanel = () => (
  <Box sx={{
    position: 'relative',
    border: '1px solid rgba(120,150,200,.24)',
    borderRadius: '14px',
    background: 'linear-gradient(180deg,#0c1830,#0a1326)',
    boxShadow: '0 30px 80px rgba(0,0,0,.45)',
    overflow: 'hidden',
  }}>
    {/* Panel titlebar */}
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '15px', py: '11px', borderBottom: '1px solid rgba(120,150,200,.18)', background: 'rgba(8,15,30,.6)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: MONO, fontSize: '11.5px', letterSpacing: '.08em', color: '#90a6cc' }}>
        <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: '#2dd4bf' }} />
        ACAD2010 — P42-1010 · SHT 1/1
      </Box>
      <Box sx={{ fontFamily: MONO, fontSize: '11px', color: '#f5a524', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#f5a524', animation: 'pulseDot 1.2s infinite', '@keyframes pulseDot': { '0%,100%': { opacity: .35 }, '50%': { opacity: 1 } } }} />
        DETECTING
      </Box>
    </Box>

    {/* SVG schematic */}
    <Box sx={{ p: '6px' }}>
      <Box
        component="svg"
        viewBox="0 0 620 400"
        sx={{
          display: 'block',
          width: '100%',
          height: 'auto',
          '& .box-in': {
            animation: 'boxIn .55s cubic-bezier(.2,.8,.3,1) both',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 1, transform: 'none' },
          },
          '& .tag-in': {
            animation: 'tagIn .4s ease both',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 1, transform: 'none' },
          },
          '& .scan': {
            animation: 'scanSweep 5.5s ease-in-out infinite',
            '@media (prefers-reduced-motion: reduce)': { display: 'none' },
          },
          '@keyframes boxIn': { '0%': { opacity: 0, transform: 'scale(.86)' }, '100%': { opacity: 1, transform: 'scale(1)' } },
          '@keyframes tagIn': { '0%': { opacity: 0, transform: 'translateY(6px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
          '@keyframes scanSweep': { '0%': { transform: 'translateX(-30px)', opacity: 0 }, '6%': { opacity: .9 }, '60%': { opacity: .9 }, '70%': { opacity: 0 }, '100%': { transform: 'translateX(640px)', opacity: 0 } },
          '@keyframes march': { to: { strokeDashoffset: '-200' } },
        }}
        fontFamily={MONO}
      >
        <defs>
          <linearGradient id="scanGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#2dd4bf" stopOpacity="0" />
            <stop offset="1" stopColor="#2dd4bf" stopOpacity=".22" />
          </linearGradient>
        </defs>

        {/* Base pipes */}
        <g stroke="#52688f" strokeWidth="2" fill="none" strokeLinejoin="round" strokeLinecap="round">
          <polyline points="79,118 171,118 250,118 250,90" />
          <polyline points="80,292 80,166" />
          <polyline points="100,318 196,318 322,318 322,326" />
          <polyline points="322,150 322,118 250,118" />
          <polyline points="370,90 430,90 522,90 522,114" />
          <polyline points="344,250 412,250 522,250 522,150" />
          <polyline points="360,330 360,318" />
        </g>

        {/* Base symbols */}
        <g stroke="#7c93bd" strokeWidth="1.7" fill="none">
          <rect x="46" y="70" width="66" height="96" rx="9" />
          <path d="M46 84 Q79 70 112 84" />
          <circle cx="80" cy="312" r="20" />
          <polygon points="80,300 92,318 68,318" fill="#7c93bd" fillOpacity=".25" />
          <rect x="250" y="64" width="120" height="48" rx="4" />
          <path d="M262 88 H358" strokeDasharray="3 5" />
          <rect x="300" y="150" width="44" height="176" rx="20" />
          <path d="M300 196 H344 M300 240 H344 M300 284 H344" strokeOpacity=".5" />
          <rect x="474" y="92" width="96" height="56" rx="28" />
          <polygon points="180,306 196,318 180,330" />
          <polygon points="212,306 196,318 212,330" />
          <circle cx="186" cy="118" r="15" />
          <circle cx="430" cy="250" r="15" />
          <circle cx="360" cy="338" r="15" />
        </g>
        <g fill="#90a6cc" fontSize="9" textAnchor="middle">
          <text x="186" y="121">FT</text>
          <text x="430" y="253">PT</text>
          <text x="360" y="341">TT</text>
        </g>

        {/* Amber marching connection traces */}
        <g stroke="#f5a524" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeDasharray="2 9" style={{ animation: 'march 4s linear infinite' }}>
          <polyline points="79,118 171,118 250,118 250,90" />
          <polyline points="370,90 430,90 522,90 522,114" />
          <polyline points="100,318 196,318 322,318" />
        </g>

        {/* Detection overlays */}
        <g className="box-in" style={{ transformBox: 'fill-box', transformOrigin: 'center', animationDelay: '.25s' }}>
          <rect x="38" y="58" width="82" height="120" rx="3" fill="rgba(45,212,191,.06)" stroke="#2dd4bf" strokeWidth="1.6" strokeDasharray="6 4" />
        </g>
        <g className="tag-in" style={{ animationDelay: '.45s' }}>
          <rect x="38" y="44" width="50" height="15" rx="3" fill="#08111f" stroke="#2dd4bf" strokeWidth="1" />
          <text x="44" y="55" fill="#2dd4bf" fontSize="10">T-300</text>
        </g>

        <g className="box-in" style={{ transformBox: 'fill-box', transformOrigin: 'center', animationDelay: '.55s' }}>
          <rect x="52" y="284" width="56" height="56" rx="3" fill="rgba(45,212,191,.06)" stroke="#2dd4bf" strokeWidth="1.6" strokeDasharray="6 4" />
        </g>
        <g className="tag-in" style={{ animationDelay: '.75s' }}>
          <rect x="52" y="270" width="48" height="15" rx="3" fill="#08111f" stroke="#2dd4bf" strokeWidth="1" />
          <text x="58" y="281" fill="#2dd4bf" fontSize="10">P-101</text>
        </g>

        <g className="box-in" style={{ transformBox: 'fill-box', transformOrigin: 'center', animationDelay: '.85s' }}>
          <rect x="242" y="54" width="136" height="68" rx="3" fill="rgba(45,212,191,.06)" stroke="#2dd4bf" strokeWidth="1.6" strokeDasharray="6 4" />
        </g>
        <g className="tag-in" style={{ animationDelay: '1.05s' }}>
          <rect x="242" y="40" width="50" height="15" rx="3" fill="#08111f" stroke="#2dd4bf" strokeWidth="1" />
          <text x="248" y="51" fill="#2dd4bf" fontSize="10">E-204</text>
        </g>

        <g className="box-in" style={{ transformBox: 'fill-box', transformOrigin: 'center', animationDelay: '1.15s' }}>
          <rect x="290" y="140" width="64" height="196" rx="3" fill="rgba(45,212,191,.06)" stroke="#2dd4bf" strokeWidth="1.6" strokeDasharray="6 4" />
        </g>
        <g className="tag-in" style={{ animationDelay: '1.35s' }}>
          <rect x="290" y="126" width="52" height="15" rx="3" fill="#08111f" stroke="#2dd4bf" strokeWidth="1" />
          <text x="296" y="137" fill="#2dd4bf" fontSize="10">C-100</text>
        </g>

        {/* D-220 (confirmed = green) */}
        <g className="box-in" style={{ transformBox: 'fill-box', transformOrigin: 'center', animationDelay: '1.45s' }}>
          <rect x="464" y="82" width="116" height="76" rx="3" fill="rgba(68,208,123,.07)" stroke="#44d07b" strokeWidth="1.6" />
        </g>
        <g className="tag-in" style={{ animationDelay: '1.65s' }}>
          <rect x="464" y="68" width="68" height="15" rx="3" fill="#08111f" stroke="#44d07b" strokeWidth="1" />
          <text x="470" y="79" fill="#44d07b" fontSize="10">D-220 ✓</text>
        </g>

        {/* V-12 (amber connection) */}
        <g className="box-in" style={{ transformBox: 'fill-box', transformOrigin: 'center', animationDelay: '1.75s' }}>
          <rect x="170" y="300" width="52" height="36" rx="3" fill="rgba(245,165,36,.06)" stroke="#f5a524" strokeWidth="1.6" strokeDasharray="5 4" />
        </g>
        <g className="tag-in" style={{ animationDelay: '1.95s' }}>
          <rect x="170" y="338" width="42" height="15" rx="3" fill="#08111f" stroke="#f5a524" strokeWidth="1" />
          <text x="176" y="349" fill="#f5a524" fontSize="10">V-12</text>
        </g>

        {/* Scan line */}
        <g className="scan">
          <rect x="-2" y="6" width="3" height="388" fill="#2dd4bf" />
          <rect x="-26" y="6" width="48" height="388" fill="url(#scanGrad)" />
        </g>
      </Box>
    </Box>

    {/* Scan stats footer — counts up in sync with the scan beam */}
    <Box sx={{ borderTop: '1px solid rgba(120,150,200,.15)', background: 'rgba(8,15,30,.7)', px: '20px', py: '14px', display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
      <ScanStat value={52} label="equipment" />
      <Box sx={{ width: '1px', alignSelf: 'stretch', background: 'rgba(120,150,200,.18)' }} />
      <ScanStat value={48} label="connections" />
      <Box sx={{ width: '1px', alignSelf: 'stretch', background: 'rgba(120,150,200,.18)' }} />
      <ScanStat value={99.2} label="confidence" decimals={1} suffix="%" fadeIn startDelay={2200} />
    </Box>
  </Box>
);

// ── Step card ────────────────────────────────────────────────────────────────
const StepCard = ({ num, title, body, icon, dark = false }) => (
  <Box sx={{
    background: dark ? '#0a1326' : '#fff',
    border: `1px solid ${dark ? '#0a1326' : '#dde3ec'}`,
    borderRadius: '14px',
    p: '26px 22px 28px',
    position: 'relative',
    overflow: 'hidden',
    color: dark ? '#f4f8ff' : '#0e1726',
  }}>
    {dark && <EngGrid opacity={0.4} animate={false} sx={{ position: 'absolute', inset: 0 }} />}
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ fontFamily: MONO, fontSize: '12px', color: dark ? '#5f7398' : '#9aa8c0', letterSpacing: '.1em' }}>{num}</Box>
      <Box sx={{ mt: '18px', mb: '16px', width: '46px', height: '46px', border: `1.5px solid ${dark ? '#2dd4bf' : icon.color}`, borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box component="svg" viewBox="0 0 24 24" sx={{ width: '22px', height: '22px' }} fill="none" stroke={dark ? '#2dd4bf' : icon.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          {icon.paths}
        </Box>
      </Box>
      <Box sx={{ m: 0, mb: '8px', fontSize: '18px', fontWeight: 600, color: dark ? '#f4f8ff' : '#0e1726' }}>{title}</Box>
      <Box sx={{ m: 0, fontSize: '14.5px', lineHeight: 1.55, color: dark ? '#aebfdd' : '#5a6b85' }}>{body}</Box>
    </Box>
  </Box>
);

// ── Main component ───────────────────────────────────────────────────────────
const Welcome = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  return (
    <Box sx={{ background: '#0a1326', overflowX: 'hidden' }}>
      <Navbar />

      {/* ── HERO ── */}
      <Box
        component="header"
        sx={{
          position: 'relative',
          background: 'radial-gradient(1100px 600px at 78% -8%, rgba(47,109,240,.20), transparent 60%), radial-gradient(900px 500px at 12% 110%, rgba(45,212,191,.10), transparent 55%), #0a1326',
          pt: '74px',
          pb: '86px',
          px: '28px',
        }}
      >
        <EngGrid opacity={0.5} />
        {/* Drawing border tick */}
        <Box sx={{ position: 'absolute', top: '18px', left: '28px', right: '28px', height: '1px', background: 'rgba(120,150,200,.18)', pointerEvents: 'none' }} />

        <Box
          sx={{
            position: 'relative',
            maxWidth: '1200px',
            mx: 'auto',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.02fr 1.18fr' },
            gap: '54px',
            alignItems: 'center',
          }}
        >
          {/* Left: copy */}
          <Box>
            <Kicker variant="pill" sx={{ mb: '26px' }}>P&ID Intelligence</Kicker>
            <Box
              component="h1"
              sx={{
                m: 0,
                mb: '20px',
                fontSize: 'clamp(40px,5.1vw,62px)',
                lineHeight: 1.04,
                fontWeight: 700,
                letterSpacing: '-.02em',
                color: '#f4f8ff',
                textWrap: 'balance',
              }}
            >
              Turn P&IDs into<br />structured, validated data.
            </Box>
            <Box sx={{ m: 0, mb: '34px', fontSize: '19px', lineHeight: 1.6, color: '#aebfdd', maxWidth: '520px' }}>
              PANDID reads your Piping & Instrumentation Diagrams — detecting elements, lines and tags — then lets you verify, correct and export with engineering-grade precision.
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px', mb: '34px' }}>
              <AmberButton
                to={isAuthenticated ? '/dashboard' : '/register'}
                size="lg"
                sx={{ fontSize: '15.5px', py: '14px', px: '26px' }}
              >
                {isAuthenticated ? 'Go to Dashboard' : 'Start extracting'} <Box component="span" sx={{ fontFamily: MONO }}>→</Box>
              </AmberButton>
              <Box
                component={RouterLink}
                to="/learn-more"
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: '9px', textDecoration: 'none',
                  background: 'transparent', color: '#dce6fa', fontWeight: 600, fontSize: '15.5px',
                  py: '14px', px: '24px', borderRadius: '10px', border: '1px solid rgba(120,150,200,.34)',
                  transition: 'border-color .15s, color .15s',
                  '&:hover': { borderColor: 'rgba(45,212,191,.6)', color: '#fff' },
                }}
              >
                See how it works
              </Box>
            </Box>

          </Box>

          {/* Right: animated detection panel */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <DetectionPanel />
          </Box>
        </Box>

        {/* Full-width connections table — sits below the 2-col grid */}
        <Box sx={{ position: 'relative', maxWidth: '1200px', mx: 'auto', mt: '40px', display: { xs: 'none', md: 'block' } }}>
          <Box sx={{ border: '1px solid rgba(120,150,200,.22)', borderRadius: '12px', background: 'rgba(8,15,30,.55)', overflow: 'hidden' }}>
            {/* Table header bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '20px', py: '11px', borderBottom: '1px solid rgba(120,150,200,.15)', background: 'rgba(8,15,30,.5)' }}>
              <Box sx={{ fontFamily: MONO, fontSize: '11px', letterSpacing: '.12em', color: '#7f93b8' }}>EXTRACTED · process graph</Box>
            </Box>
            <Box sx={{ px: '20px', py: '14px' }}>
              {/* Column headers */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr .32fr 1fr', gap: '0 20px', fontFamily: MONO, mb: '10px' }}>
                <Box sx={{ color: '#5f7398', fontSize: '10.5px', letterSpacing: '.1em' }}>FROM</Box>
                <Box sx={{ color: '#5f7398', fontSize: '10.5px', letterSpacing: '.1em', textAlign: 'center' }}>RELATIONSHIP</Box>
                <Box sx={{ color: '#5f7398', fontSize: '10.5px', letterSpacing: '.1em' }}>TO</Box>
              </Box>
              {[
                { from: { tag: 'D-001',  type: 'drum'            }, rel: 'flows_to',    to: { tag: 'P-007', type: 'centrifugal pump' }, delay: '.5s'  },
                { from: { tag: 'P-007',  type: 'centrifugal pump' }, rel: 'flows_to',       to: { tag: 'E-102', type: 'heat exchanger'   }, delay: '.8s'  },
                { from: { tag: 'T-300', type: 'vessel'           }, rel: 'senses',   to: { tag: 'E-102',  type: 'heat exchanger'  }, delay: '1.1s' },
                { from: { tag: 'FCV-12', type: 'control valve'   }, rel: 'controls',    to: { tag: 'T-300', type: 'vessel'           }, delay: '1.4s' },
                { from: { tag: 'T-300',  type: 'vessel'          }, rel: 'flows_to', to: { tag: 'V-205', type: 'separator'        }, delay: '1.7s' },
              ].map(({ from, rel, to, delay }) => (
                <Box key={delay} sx={{ display: 'grid', gridTemplateColumns: '1fr .32fr 1fr', gap: '0 20px', mb: '8px', fontFamily: MONO, fontSize: '12px', lineHeight: 1.55, animation: `rowIn .5s ease ${delay} both`, '@keyframes rowIn': { '0%': { opacity: 0, transform: 'translateY(8px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } }, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}>
                  <Box sx={{ whiteSpace: 'nowrap' }}>
                    <Box component="span" sx={{ color: '#5f7398' }}>[</Box><Box component="span" sx={{ color: '#6a82a8' }}>equipment: </Box><Box component="span" sx={{ color: '#2dd4bf' }}>{from.tag}</Box><Box component="span" sx={{ color: '#5f7398' }}>, </Box><Box component="span" sx={{ color: '#6a82a8' }}>type: </Box><Box component="span" sx={{ color: '#dce6fa' }}>{from.type}</Box><Box component="span" sx={{ color: '#5f7398' }}>]</Box>
                  </Box>
                  <Box sx={{ color: '#f5a524', textAlign: 'center' }}>{rel}</Box>
                  <Box sx={{ whiteSpace: 'nowrap' }}>
                    <Box component="span" sx={{ color: '#5f7398' }}>[</Box><Box component="span" sx={{ color: '#6a82a8' }}>equipment: </Box><Box component="span" sx={{ color: '#2dd4bf' }}>{to.tag}</Box><Box component="span" sx={{ color: '#5f7398' }}>, </Box><Box component="span" sx={{ color: '#6a82a8' }}>type: </Box><Box component="span" sx={{ color: '#dce6fa' }}>{to.type}</Box><Box component="span" sx={{ color: '#5f7398' }}>]</Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── SPEC STRIP ── */}
      <Box sx={{ background: '#070e1c', borderTop: '1px solid rgba(120,150,200,.16)', borderBottom: '1px solid rgba(120,150,200,.16)', py: '22px', px: '28px' }}>
        <Box sx={{ maxWidth: '1200px', mx: 'auto', display: 'flex', flexWrap: { xs: 'wrap', lg: 'nowrap' }, alignItems: 'center', gap: '14px 30px', fontFamily: MONO, fontSize: '12.5px', color: '#8298bd' }}>
          <Box component="span" sx={{ color: '#5f7398', letterSpacing: '.12em' }}>INPUTS</Box>
          {['PDF', 'PNG', 'JPG', 'TIFF'].map((f, i, a) => (
            <React.Fragment key={f}>
              <Box component="span" sx={{ color: '#dce6fa' }}>{f}</Box>
              {i < a.length - 1 && <Box component="span" sx={{ color: '#3a4d70' }}>·</Box>}
            </React.Fragment>
          ))}
          <Box sx={{ width: '1px', height: '18px', background: 'rgba(120,150,200,.22)' }} />
          <Box component="span" sx={{ color: '#5f7398', letterSpacing: '.12em' }}>DETECTS</Box>
          {['equipment', 'instruments', 'lines', 'tags'].map((d, i, a) => (
            <React.Fragment key={d}>
              <Box component="span" sx={{ color: '#dce6fa' }}>{d}</Box>
              {i < a.length - 1 && <Box component="span" sx={{ color: '#3a4d70' }}>·</Box>}
            </React.Fragment>
          ))}
          <Box sx={{ width: '1px', height: '18px', background: 'rgba(120,150,200,.22)' }} />
          <Box component="span" sx={{ color: '#5f7398', letterSpacing: '.12em' }}>EXPORTS</Box>
          {['CSV', 'graph DB'].map((e, i, a) => (
            <React.Fragment key={e}>
              <Box component="span" sx={{ color: '#2dd4bf' }}>{e}</Box>
              {i < a.length - 1 && <Box component="span" sx={{ color: '#3a4d70' }}>·</Box>}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      {/* ── HOW IT WORKS (light) ── */}
      <Box component="section" sx={{ background: '#eef1f6', color: '#0e1726', py: '90px', px: '28px' }}>
        <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '18px', mb: '54px' }}>
            <Box>
              <Kicker variant="plain" light sx={{ mb: '14px' }}>workflow</Kicker>
              <Box component="h2" sx={{ m: 0, fontSize: 'clamp(30px,3.6vw,42px)', fontWeight: 700, letterSpacing: '-.02em', color: '#0e1726' }}>
                From drawing to data, in four moves.
              </Box>
            </Box>
            <Box sx={{ m: 0, maxWidth: '360px', fontSize: '16px', lineHeight: 1.6, color: '#5a6b85' }}>
              Creating the contextual centre your downstream software systems have been waiting for.
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4,1fr)' }, gap: '18px' }}>
            <StepCard num="§01" title="Upload" body="Drop a PDF, PNG, JPG or TIFF — single sheet or full drawing set."
              icon={{ color: '#0d47a1', paths: <><path d="M12 16V4M12 4l-4 4M12 4l4 4" /><path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" /></> }} />
            <StepCard num="§02" title="Detect" body="AI finds assets and instruments and the connections between them." dark
              icon={{ color: '#2dd4bf', paths: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><path d="M10 6.5h4a3 3 0 0 1 3 3V14" /></> }} />
            <StepCard num="§03" title="Verify" body="Rapidly confirm or correct, detection by detection, with the original drawing right there."
              icon={{ color: '#00838f', paths: <><path d="M20 6L9 17l-5-5" /></> }} />
            <StepCard num="§04" title="Export" body="Structured data — Excel today, graph database next — ready for your workflows."
              icon={{ color: '#0d47a1', paths: <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 9v12" /></> }} />
          </Box>
        </Box>
      </Box>

      {/* ── MASTERY OF DETAIL (dark) ── */}
      <Box component="section" sx={{ position: 'relative', background: '#0a1326', py: '96px', px: '28px', overflow: 'hidden' }}>
        <EngGrid opacity={0.4} animate={false} />
        <Box sx={{ position: 'relative', maxWidth: '1200px', mx: 'auto', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '.92fr 1.18fr' }, gap: '54px', alignItems: 'center' }}>
          <Box>
            <Kicker variant="plain" sx={{ mb: '16px' }}>mastery of detail</Kicker>
            <Box component="h2" sx={{ m: 0, mb: '20px', fontSize: 'clamp(30px,3.6vw,44px)', fontWeight: 700, letterSpacing: '-.02em', color: '#f4f8ff', lineHeight: 1.08 }}>
              Every box accountable.<br />Every line traced.
            </Box>
            <Box sx={{ mb: '30px', fontSize: '17px', lineHeight: 1.65, color: '#aebfdd', maxWidth: '480px' }}>
              Densely-packed P&IDs hide hundreds of components. PANDID surfaces each one as an editable element — select it, confirm it, move it, re-link it. Nothing is lost in the noise.
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                { dot: { border: '2px solid #2dd4bf' }, title: 'Bounding-box detection', body: 'Each element framed and tagged, overlaid on your source.' },
                { dot: { border: '2px solid #44d07b' }, title: 'Confirm & correct in place', body: 'Green when confirmed — your judgement, on the record.' },
                { dot: { border: '2px dashed #f5a524' }, title: 'Connection mapping', body: 'Pipes and links captured as real relationships, not pixels.' },
              ].map(({ dot, title, body }) => (
                <Box key={title} sx={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <Box sx={{ mt: '3px', width: '11px', height: '11px', ...dot, borderRadius: '2px', flexShrink: 0 }} />
                  <Box>
                    <Box sx={{ fontWeight: 600, color: '#eaf0fb', fontSize: '15.5px' }}>{title}</Box>
                    <Box sx={{ fontSize: '14px', color: '#8fa3c6', lineHeight: 1.5 }}>{body}</Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* App frame with screenshot */}
          <Box sx={{ position: 'relative' }}>
            <Box sx={{ border: '1px solid rgba(120,150,200,.28)', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,.5)', background: '#0c1830' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', px: '14px', py: '11px', borderBottom: '1px solid rgba(120,150,200,.18)', background: 'rgba(8,15,30,.7)' }}>
                {['#ff5f57', '#febc2e', '#28c840'].map(c => <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', background: c }} />)}
                <Box sx={{ ml: '10px', fontFamily: MONO, fontSize: '11.5px', color: '#8298bd' }}>pandid.io/drawing/p42-1010</Box>
              </Box>
              <Box component="img" src="/images/screenshot-drawing-view-2.png" alt="PANDID verification view" sx={{ display: 'block', width: '100%', height: 'auto' }} />
            </Box>
            {/* Floating callout */}
            <Box sx={{
              position: 'absolute', top: '30%', right: '-12px',
              background: '#08111f', border: '1px solid #2dd4bf', borderRadius: '8px',
              px: '11px', py: '8px', fontFamily: MONO, fontSize: '11px', color: '#2dd4bf',
              boxShadow: '0 10px 30px rgba(0,0,0,.4)',
              animation: 'floatY 5s ease-in-out infinite',
              '@keyframes floatY': { '0%,100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-7px)' } },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}>
              412 elements ✓
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── OUTCOMES (light) ── */}
      <Box component="section" sx={{ background: '#eef1f6', color: '#0e1726', py: '90px', px: '28px' }}>
        <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
          <Kicker variant="plain" light sx={{ mb: '14px' }}>outcomes</Kicker>
          <Box component="h2" sx={{ m: 0, mb: '50px', fontSize: 'clamp(30px,3.6vw,42px)', fontWeight: 700, letterSpacing: '-.02em', maxWidth: '680px', lineHeight: 1.1 }}>
            What you get back is a working data asset, not a flat file.
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3,1fr)' }, gap: '18px' }}>
            {[
              { stat: '100%', title: 'Captured, not skimmed', body: 'Every valve, instrument and line on the sheet — surfaced as a verifiable element.' },
              { stat: 'minutes', title: 'Not days of takeoff', body: 'Work that once meant hours of manual tracing, returned while the kettle boils.' },
              { stat: '1:1', title: 'A digital twin of the doc', body: 'Structured, queryable data ready for Excel today and graph databases next.' },
            ].map(({ stat, title, body }) => (
              <Box key={stat} sx={{ background: '#fff', border: '1px solid #dde3ec', borderRadius: '14px', p: '30px 26px' }}>
                <Box sx={{ fontFamily: MONO, fontSize: '34px', fontWeight: 600, color: '#0d47a1', letterSpacing: '-.02em' }}>{stat}</Box>
                <Box sx={{ height: '1px', background: '#e3e8f0', my: '18px' }} />
                <Box sx={{ fontWeight: 600, fontSize: '16.5px', mb: '7px' }}>{title}</Box>
                <Box sx={{ fontSize: '14.5px', lineHeight: 1.55, color: '#5a6b85' }}>{body}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── PRICING CTA (dark) ── */}
      <Box component="section" sx={{ position: 'relative', background: '#0a1326', py: '96px', px: '28px', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(700px 380px at 50% 0%, rgba(47,109,240,.18), transparent 60%)' }} />
        <Box sx={{ position: 'relative', maxWidth: '760px', mx: 'auto', textAlign: 'center' }}>
          <Kicker variant="plain" sx={{ display: 'inline-block', mb: '18px' }}>one simple plan</Kicker>
          <Box component="h2" sx={{ m: 0, mb: '16px', fontSize: 'clamp(32px,4vw,48px)', fontWeight: 700, letterSpacing: '-.02em', color: '#f4f8ff' }}>
            Unlimited drawings. One annual price.
          </Box>
          <Box sx={{ mx: 'auto', mb: '38px', maxWidth: '520px', fontSize: '17px', lineHeight: 1.6, color: '#aebfdd' }}>
            Everything PANDID does — detection, verification and export — with no per-sheet metering.
          </Box>

          {/* Split card */}
          <Box sx={{ display: 'inline-flex', alignItems: 'stretch', border: '1px solid rgba(120,150,200,.28)', borderRadius: '16px', overflow: 'hidden', background: 'linear-gradient(180deg,#0d1a35,#0a1326)', boxShadow: '0 30px 70px rgba(0,0,0,.5)', textAlign: 'left' }}>
            <Box sx={{ p: '30px 34px', borderRight: '1px solid rgba(120,150,200,.2)' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <Box sx={{ fontFamily: MONO, fontSize: '46px', fontWeight: 600, color: '#f4f8ff', lineHeight: 1 }}>$500</Box>
                <Box sx={{ fontFamily: MONO, fontSize: '14px', color: '#8298bd' }}>/ year</Box>
              </Box>
              <Box sx={{ fontFamily: MONO, fontSize: '12px', color: '#7f93b8', mt: '10px', letterSpacing: '.05em' }}>unlimited · all features</Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', px: '30px' }}>
              <AmberButton to={isAuthenticated ? '/checkout' : '/pricing'} sx={{ fontSize: '15.5px', py: '14px', px: '28px' }}>
                View pricing <Box component="span" sx={{ fontFamily: MONO }}>→</Box>
              </AmberButton>
            </Box>
          </Box>

          <Box sx={{ mt: '22px', fontFamily: MONO, fontSize: '12px', color: '#5f7398' }}>
            No credit card to explore · cancel anytime
          </Box>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
};

export default Welcome;
